import { getDistanceFromM } from '@/app/common/measures/getters';
import type { PartialLocalization } from '@/app/common/measures/types';
import { DESTINATION_TYPE } from '@/app/search/types';

export type VariableExtractor<T> = (item: T) => Value | undefined;

export function sorter<T>(item1: T, item2: T, variableExtractor: VariableExtractor<T>, isDesc?: boolean): number {
  let result;
  const value1 = variableExtractor(item1);
  const value2 = variableExtractor(item2);
  if (!value1 && !value2) {
    result = 0;
  } else if (!value2) {
    result = 1;
  } else if (!value1) {
    result = -1;
  } else if ((value1 > value2 && !isDesc) || (value1 < value2 && isDesc)) {
    result = 1;
  } else if ((value1 < value2 && !isDesc) || (value1 > value2 && isDesc)) {
    result = -1;
  } else {
    result = 0;
  }
  return result;
}

/**
 *
 * This function calculates the distance between two points on the Earth's surface using the Haversine formula.
 * The distance is returned in meters (m) as the final result of the calculation is multiplied by the radius
 * of the Earth (R) in meters, which is approximately 6371071 meters.
 */
export function directLineDistance(pointA: LatLng | undefined, pointB: LatLng | undefined): number {
  if (!pointA || !pointB) return 0;
  const R = 6371071;
  const rLatitudeA = (pointA.latitude * Math.PI) / 180;
  const rLatitudeB = (pointB.latitude * Math.PI) / 180;
  const difLatitude = rLatitudeB - rLatitudeA;
  const difLongitude = ((pointB.longitude - pointA.longitude) * Math.PI) / 180;

  return (
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(difLatitude / 2) ** 2 + Math.cos(rLatitudeA) * Math.cos(rLatitudeB) * Math.sin(difLongitude / 2) ** 2
      )
    )
  );
}

export function isValidFlightClass(flightClass: string | undefined): boolean {
  return ['Y', 'P', 'C', 'F'].includes(flightClass || 'undefined');
}

// internalHotelId is parsed from URL path segments (Number(...)) in a few places without
// validation; a malformed segment yields NaN or 0, which would otherwise be sent as-is to
// backend endpoints as a valid-looking id.
export function isValidHotelId(internalHotelId: number | undefined): boolean {
  return Number.isInteger(internalHotelId) && (internalHotelId as number) > 0;
}

export function keyUpHandler(callback: (event: React.KeyboardEvent) => void) {
  return (event: React.KeyboardEvent) => ['Enter'].includes(event.key) && callback(event);
}

// Activation handler for elements with role="button" that are not native <button>s.
// Fires on Enter and Space (the keys a real button responds to) and calls
// preventDefault — crucially on Space, where a <div> would otherwise scroll the page.
export function keyDownHandler(callback: (event: React.KeyboardEvent) => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      callback(event);
    }
  };
}

export function pricePerNight(
  priceShown: number,
  nights: number | undefined,
  numberOfRooms: number | undefined
): number {
  return priceShown / (nights || 1) / (numberOfRooms || 1);
}

/**
 * Maps destination type and ID to the appropriate parameters for API requests
 */
export function getDestinationParameters(
  destinationType: DESTINATION_TYPE | undefined,
  destinationId: string | undefined
) {
  return destinationType === DESTINATION_TYPE.CITY
    ? { cities: [Number(destinationId || 0)] }
    : destinationType === DESTINATION_TYPE.TAG
      ? { tag: destinationId }
      : destinationType === DESTINATION_TYPE.AIRPORT
        ? { airports: [destinationId || ''] }
        : {};
}

// Builds the `accommodation` search parameter from the results-page `prefilter_checkin_checkout`
// URL param (format: "yyyy-mm-dd,yyyy-mm-dd"). These are the partial-stay hotel dates that can be
// shorter than the flight `interval`. Pricing normally recovers them from the search via vcSearchId,
// but that id can be outdated/invalid/missing — sending the dates explicitly is the fallback.
// Dates are already in yyyy-mm-dd, so no reformatting is needed (unlike `interval`). Partial stays are
// a DP-only concept (in HO/TH the trip `interval` already is the hotel stay), so gate on `isDP`.
// The value comes from a URL param, so also reject checkout <= checkin (manipulated/malformed): callers
// then fall back gracefully (recover from vcSearchId / trip interval) instead of sending a bad interval.
export function buildAccommodationInterval(
  customCheckinCheckout: string | undefined | null,
  isDP: boolean
): ShoppingAsyncSearchParameters['accommodation'] {
  if (!isDP || !customCheckinCheckout) return undefined;
  const [start, end] = customCheckinCheckout.split(',');
  const isValidDate = (date: string | undefined): date is string => /^\d{4}-\d{2}-\d{2}$/.test(date ?? '');
  // start/end are zero-padded yyyy-mm-dd, so lexicographic comparison is chronological.
  if (!isValidDate(start) || !isValidDate(end) || start >= end) return undefined;
  return { start: { date: start }, end: { date: end } };
}

/**
 * Calculates the straight-line distance from a hotel to each airport in the provided list.
 * Uses the Haversine formula to compute distances based on latitude/longitude coordinates,
 * then converts the result to the appropriate unit (km/mi) based on localization settings.
 */
export function getDistanceFromAirportsToHotel(
  localization: PartialLocalization,
  airports?: Airport[],
  hotelCoordinates?: LatLng
): AirportDistance[] {
  const { lang, locale, country } = localization;
  return (
    airports?.map((airport) => {
      const distance = directLineDistance(hotelCoordinates, airport.coordinates);
      const { value, unit } = getDistanceFromM(distance, { lang, locale, country });

      return {
        iata: airport.id,
        airportLatitude: airport.coordinates.latitude,
        airportLongitude: airport.coordinates.longitude,
        distanceWithUnit: {
          value,
          unit,
        },
      };
    }) || []
  );
}
